import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { devRequestService } from "../services/dataService";
import { DevRequestDetail, DevRequestImpact as Im, DevRequestListItem, DevRequestPriority as P, DevRequestStatus as S, DevRequestType as T } from "../types";
import { usePermissions } from "../context/PermissionContext";
import { relTime } from "../components/NotificationBell";
import {
  DR_PRIORITY_BADGE, DR_PRIORITY_LABEL, DR_STATUS_CLASS, DR_STATUS_LABEL, DR_TYPE, DR_TYPES, DR_WAIT_DAYS, DrFlag,
  daysSince, drErr, drFlags, drFmtDate, drFmtDateTime, drIsOpen, drLastBy, drMatches, drTopRank,
} from "../utils/devRequests";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import DevRequestDrawer from "../components/DevRequestDrawer";
import DevRequestModal from "../components/DevRequestModal";
import DevRequestActionModal from "../components/DevRequestActionModal";
import "./PurchaseOrders.css";
import "./DevRequests.css";

/**
 * Geliştirme Talepleri — tek rota (/dev-requests/*):
 *   /dev-requests          liste
 *   /dev-requests/<id>     liste + talep kartı (çekmece) — bildirim bağlantıları buraya gelir
 *   /dev-requests/new      yeni talep modalı açılır, adres /dev-requests'e döner
 * Yetki: create = yalnız kendi talepleri; view = hepsini okur; update = hepsini görür, iç not yazar, düzenler.
 * Durum ve hedef tarih yalnız Nedaris Destek'ten gelir (atama yok); panelde talep sahibi geri çeker / yeniden açar.
 * Onay, bildirim ve yenileme bu sayfada; çekmece ve modallar sonucu buraya bildirir.
 */

type Chip = "open" | "new" | "team" | "info" | "closed" | "all";
const CHIPS: [Chip, string][] = [["open", "Açık"], ["new", "Yeni"], ["team", "Ekipte"], ["info", "Bilgi bekleniyor"], ["closed", "Kapanan"], ["all", "Hepsi"]];
const TEAM: S[] = [S.InReview, S.Planned, S.InProgress];
const chipMatch = (c: Chip, s: S) =>
  c === "all" ? true : c === "open" ? drIsOpen(s) : c === "new" ? s === S.New : c === "team" ? TEAM.includes(s)
    : c === "info" ? s === S.NeedsInfo : !drIsOpen(s);
type Scope = "all" | "mine";
const SCOPES: [Scope, string][] = [["all", "Tümü"], ["mine", "Benim"]];
const scopeMatch = (sc: Scope, r: DevRequestListItem) => (sc === "all" ? true : r.isMine);
type Focus = "" | "stale" | "waiting";

const NOT_FOUND = "Talep bulunamadı; silinmiş olabilir. Liste yenilendi.";
const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ms = (iso?: string | null) => (iso ? new Date(iso).getTime() : 0);
const isBlocking = (r: DevRequestListItem) => drIsOpen(r.status) && r.impact === Im.Blocking;
/** Son 30 günde tamamlananlar ve ortalama çözüm süresi (açılıştan kapanışa, gün). */
const doneStats = (list: DevRequestListItem[]) => {
  const done = list.filter((r) => r.status === S.Done && r.closedAt && (daysSince(r.closedAt) ?? 99) < 30);
  const avg = done.length ? done.reduce((a, r) => a + (ms(r.closedAt) - ms(r.createdAt)), 0) / done.length / 86400000 : 0;
  return { n: done.length, avg: avg.toLocaleString("tr-TR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) };
};

const I = {
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>,
  refresh: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12a8 8 0 1 1-2.3-5.6" /><path d="M20 4v5h-5" /></svg>,
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  eye: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></svg>,
  filterOff: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 5h16l-6 7v6l-4 2v-8z" /><path d="m3 3 18 18" /></svg>,
  inbox: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z" /><path d="M4 13h5l2 3h2l2-3h5" /></svg>,
  warn: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3 2 20h20z" /><path d="M12 10v4M12 17.5v.5" /></svg>,
  msg: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 12a8.5 8.5 0 0 1-12.4 7.5L3 21l1.5-5.6A8.5 8.5 0 1 1 21 12Z" /></svg>,
  clip: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m21.4 11.1-9.2 9.2a6 6 0 0 1-8.5-8.5l9.2-9.2a4 4 0 0 1 5.7 5.7l-9.2 9.2a2 2 0 0 1-2.8-2.8l8.5-8.5" /></svg>,
};

const DevRequests: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const seg = (useParams()["*"] || "").split("/")[0]; // "" | "new" | <id>
  // Yalnız GUID biçimli parça kart açar (ör. /dev-requests/eski-adres çekmeceyi bozmasın); API kimlikleri küçük harf döner
  const drawerId = GUID_RE.test(seg) ? seg.toLowerCase() : null;
  const { has, loaded: permsLoaded } = usePermissions();
  const handler = has("dev-requests:update");
  const canCreate = has("dev-requests:create");
  const seeAll = handler || has("dev-requests:view");
  // Yalnız görüntüleme yetkisi olan da herkesin talebini görür: varsayılan kapsam "Tümü" (yoksa boş liste açılırdı)
  const defaultScope: Scope = seeAll ? "all" : "mine";

  const [items, setItems] = useState<DevRequestListItem[]>([]);
  const [initial, setInitial] = useState(true);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [loaded, setLoaded] = useState(false); // en az bir kez başarıyla yüklendi
  const [q, setQ] = useState("");
  const [chip, setChip] = useState<Chip>("open");
  const [scope, setScope] = useState<Scope>("mine");
  const [typeFilter, setTypeFilter] = useState<T | 0>(0);
  const [focusFilter, setFocusFilter] = useState<Focus>("");
  const [blockingOnly, setBlockingOnly] = useState(false);
  const [modal, setModal] = useState<{ open: boolean; request: DevRequestDetail | null }>({ open: false, request: null });
  const [action, setAction] = useState<{ to: S.Withdrawn | S.InReview; request: DevRequestDetail } | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // Yanıt taslakları sayfa ömrünce talep başına tutulur: kart kapanınca ya da başka talebe geçince kaybolmaz
  const drafts = useRef(new Map<string, { text: string; internal: boolean }>());
  const [alert, setAlert] = useState<{ isOpen: boolean; message: string; type: "info" | "success" | "warning" | "error" }>({ isOpen: false, message: "", type: "info" });
  const [confirm, setConfirm] = useState<{ isOpen: boolean; title: string; message: string; type: "info" | "warning" | "danger"; confirmText: string; onConfirm: () => void }>({ isOpen: false, title: "", message: "", type: "info", confirmText: "Onayla", onConfirm: () => {} });
  const fail = (message: string) => setAlert({ isOpen: true, message, type: "error" });
  const warn = (message: string) => setAlert({ isOpen: true, message, type: "warning" });
  const closeConfirm = () => setConfirm((c) => ({ ...c, isOpen: false }));

  const load = useCallback(async () => {
    setLoadError("");
    try {
      setItems(await devRequestService.list()); setLoaded(true);
    } catch (e: any) { setLoadError(drErr(e, "Talepler yüklenemedi.")); }
    finally { setInitial(false); }
  }, []);
  // Yetkiler gelmeden yüklenmez: /new derin bağlantısındaki yetki kontrolü ve varsayılan kapsam yetkiye bağlı
  useEffect(() => { if (permsLoaded) load(); }, [load, permsLoaded]);
  useEffect(() => { if (permsLoaded) setScope(seeAll ? "all" : "mine"); }, [permsLoaded, seeAll]);

  const refresh = async () => { setBusy(true); await load(); setRefreshKey((k) => k + 1); setBusy(false); };

  // /dev-requests/new → modal; adres listeye döner (yenilemede modal tekrar açılmaz)
  useEffect(() => {
    if (seg !== "new" || initial) return;
    if (canCreate) setModal({ open: true, request: null });
    else warn("Talep oluşturma yetkiniz yok.");
    navigate("/dev-requests", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seg, initial]);
  // Tanınmayan alt adres (ör. /dev-requests/eski-adres) listeye döner
  useEffect(() => {
    if (seg && seg !== "new" && !drawerId) navigate("/dev-requests", { replace: true });
  }, [seg, drawerId, navigate]);

  const openDrawer = (id: string) => navigate(`/dev-requests/${id}`, { state: { fromList: true } });
  const closeDrawer = () => ((location.state as any)?.fromList ? navigate(-1) : navigate("/dev-requests", { replace: true }));

  // ---- Türetilmiş ----
  const flags = useMemo(() => new Map<string, DrFlag[]>(items.map((r) => [r.devRequestId, drFlags(r, handler)])), [items, handler]);
  // Satır "geç": bir bayrak geç ya da talep sahibinden 7 günden uzun süredir bilgi bekleniyor
  const lateIds = useMemo(() => new Set(items
    .filter((r) => flags.get(r.devRequestId)!.some((f) => f.state === "late") || (r.status === S.NeedsInfo && (daysSince(r.statusChangedAt) ?? 0) >= DR_WAIT_DAYS))
    .map((r) => r.devRequestId)), [items, flags]);
  // Kapsam ve tür "hangi talepler" sorusudur: durum çipi sayıları bunlar üzerinden sayılır
  const pool = useMemo(() => items.filter((r) => scopeMatch(scope, r) && (!typeFilter || r.type === typeFilter)), [items, scope, typeFilter]);
  const counts = useMemo(() => Object.fromEntries(CHIPS.map(([k]) => [k, pool.filter((r) => chipMatch(k, r.status)).length])) as Record<Chip, number>, [pool]);
  const blockingCount = useMemo(() => pool.filter(isBlocking).length, [pool]);
  const rows = useMemo(() => {
    const rank = (r: DevRequestListItem) => drTopRank(flags.get(r.devRequestId)!);
    const closed = (r: DevRequestListItem) => (drIsOpen(r.status) ? 0 : 1);
    const asking = (r: DevRequestListItem) => (r.isMine && r.status === S.NeedsInfo ? 0 : 1);
    return pool.filter((r) => {
      if (!chipMatch(chip, r.status)) return false;
      if (blockingOnly && !isBlocking(r)) return false;
      if (focusFilter === "stale" && !flags.get(r.devRequestId)!.some((f) => f.key === "stale")) return false;
      if (focusFilter === "waiting" && (r.status !== S.NeedsInfo || (!handler && !r.isMine))) return false;
      return drMatches(r, q);
    }).sort((a, b) => handler
      ? closed(a) - closed(b) || Number(isBlocking(b)) - Number(isBlocking(a)) || (b.priority ?? 0) - (a.priority ?? 0)
        || rank(b) - rank(a) || ms(b.lastActivityAt) - ms(a.lastActivityAt)
      : asking(a) - asking(b) || closed(a) - closed(b) || ms(b.lastActivityAt) - ms(a.lastActivityAt));
  }, [pool, flags, handler, chip, blockingOnly, focusFilter, q]);
  const attention = useMemo(() => items
    .map((r) => ({ r, f: flags.get(r.devRequestId)! }))
    .filter((x) => x.f.length > 0)
    .sort((x, y) => drTopRank(y.f) - drTopRank(x.f) || ms(x.r.createdAt) - ms(y.r.createdAt))
    .slice(0, 6), [items, flags]);
  // KPI: yönetenler tüm görünen talepler, diğerleri yalnız kendi talepleri
  const kpi = useMemo(() => {
    const base = handler ? items : items.filter((r) => r.isMine);
    const by = (f: (r: DevRequestListItem) => boolean) => base.filter(f).length;
    const team = base.filter((r) => TEAM.includes(r.status));
    return {
      fresh: by((r) => r.status === S.New),
      stale: by((r) => flags.get(r.devRequestId)!.some((f) => f.key === "stale")),
      team: team.length,
      teamPlanned: team.filter((r) => r.status === S.Planned).length,
      teamProgress: team.filter((r) => r.status === S.InProgress).length,
      waiting: by((r) => r.status === S.NeedsInfo),
      waitingLate: by((r) => r.status === S.NeedsInfo && (daysSince(r.statusChangedAt) ?? 0) >= DR_WAIT_DAYS),
      open: by((r) => drIsOpen(r.status)),
      review: by((r) => r.status === S.InReview),
      planned: by((r) => r.status === S.Planned || r.status === S.InProgress),
      total: base.length,
      closed: by((r) => !drIsOpen(r.status)),
      done: doneStats(base),
    };
  }, [items, flags, handler]);
  const head = useMemo(() => {
    const open = rows.filter((r) => drIsOpen(r.status));
    return { open: open.length, urgent: open.filter((r) => r.priority === P.Urgent).length, high: open.filter((r) => r.priority === P.High).length };
  }, [rows]);
  const filterOn = !!q || chip !== "open" || scope !== defaultScope || typeFilter !== 0 || !!focusFilter || blockingOnly;
  const resetFilters = () => { setQ(""); setChip("open"); setScope(defaultScope); setTypeFilter(0); setFocusFilter(""); setBlockingOnly(false); };
  // KPI/acil süzgeci açılırken durum çipi o talepleri dışarıda bırakıyorsa "Açık"a döner (boş liste göstermesin)
  const toggleFocus = (f: Exclude<Focus, "">) => {
    if (focusFilter === f) return setFocusFilter("");
    setFocusFilter(f);
    if (!chipMatch(chip, f === "stale" ? S.New : S.NeedsInfo)) setChip("open");
  };
  const toggleBlocking = () => { if (!blockingOnly && chip === "closed") setChip("open"); setBlockingOnly((v) => !v); };

  // ---- İşlemler (tek yerde: onay, bildirim, yenileme) ----
  const after = async (msg?: string, type: "success" | "error" = "success") => {
    await load(); setRefreshKey((k) => k + 1);
    if (msg) setAlert({ isOpen: true, message: msg, type });
  };
  const opFail = (e: any, id: string, fallback: string) => {
    if (e?.response?.status !== 404) return fail(drErr(e, fallback));
    fail(NOT_FOUND);
    drafts.current.delete(id);
    if (drawerId === id) closeDrawer();
    load();
  };
  const newRequest = () => setModal({ open: true, request: null });
  const editRequest = (d: DevRequestDetail) => setModal({ open: true, request: d });
  const startAction = (d: DevRequestDetail, to: S.Withdrawn | S.InReview) => setAction({ request: d, to });
  const deleteRequest = (d: Pick<DevRequestListItem, "devRequestId" | "number">) => setConfirm({
    isOpen: true, type: "danger", title: "Talebi sil", confirmText: "Sil",
    message: `${d.number} ve tüm yazışmaları, dosyaları kalıcı olarak silinecek.`,
    onConfirm: async () => {
      closeConfirm(); setBusy(true);
      try {
        await devRequestService.remove(d.devRequestId);
        drafts.current.delete(d.devRequestId);
        if (drawerId === d.devRequestId) closeDrawer();
        await after("Talep silindi.");
      } catch (e) { opFail(e, d.devRequestId, "Talep silinemedi."); }
      finally { setBusy(false); }
    },
  });
  // 409 (durum bu arada değişti) da buraya gelir: pencere kapanır, sunucu metni gösterilir, liste ve kart yenilenir
  const onActionDone = (msg: string, isError?: boolean) => { setAction(null); after(msg, isError ? "error" : "success"); };
  // Çekmece yazışma/dosya değişikliğinden sonra mesajsız, talep bulunamazsa (404) NOT_FOUND ile çağırır
  const onDrawerChanged = (msg?: string) => { after(msg, msg === NOT_FOUND ? "error" : "success"); };
  const onModalSaved = async (r: { id: string; number: string }) => {
    const edited = !!modal.request;
    const id = r.id.toLowerCase();
    setModal({ open: false, request: null });
    if (drawerId !== id) navigate(`/dev-requests/${id}`, { state: { fromList: true } });
    await after(edited ? "Talep güncellendi." : `${r.number} oluşturuldu; talepleri yönetenlere iletildi.`);
  };

  if (initial) return <PageLoader message="Talepler yükleniyor..." />;

  return (
    <div className="po-page">
      <header className="po-head">
        <div>
          <span className="po-kicker">Destek</span>
          <h1>Geliştirme Talepleri</h1>
          <p>{handler
            ? "Kullanıcıların hata bildirimleri ve geliştirme istekleri. Durum ve hedef tarih Nedaris Destek tarafından güncellenir; siz yazışıp iç not ekleyebilirsiniz."
            : "Nedaris'te gördüğünüz hataları ve istediğiniz geliştirmeleri iletin; durumunu ve yanıtları buradan izleyin."}</p>
        </div>
        <div className="po-head-actions">
          <button type="button" className="po-btn" onClick={refresh} disabled={busy}><span className={busy ? "po-spin-icon" : ""} style={{ display: "inline-flex" }}>{I.refresh}</span> Yenile</button>
          {canCreate && <button type="button" className="po-btn primary" onClick={newRequest}>{I.plus} Yeni talep</button>}
        </div>
      </header>

      {loadError && <div className="po-note err" role="alert">{loadError} <button type="button" className="po-link" onClick={refresh}>Yeniden dene</button></div>}

      {loaded && (handler ? (
        <section className="po-kpis">
          <button type="button" className={`po-kpi ${kpi.stale > 0 ? "err" : "navy"} ${focusFilter === "stale" ? "on" : ""}`} aria-pressed={focusFilter === "stale"} onClick={() => toggleFocus("stale")}>
            <span className="l">Yeni</span><span className="v">{kpi.fresh}</span>
            <span className="s">{kpi.stale > 0 ? <><b>{kpi.stale}</b> tanesi 3 günden uzun yanıtsız · süzmek için tıklayın</> : "hepsine bakıldı"}</span>
          </button>
          <div className="po-kpi"><span className="l">Ekipte</span><span className="v">{kpi.team}</span><span className="s"><b>{kpi.teamPlanned}</b> planlı · <b>{kpi.teamProgress}</b> geliştiriliyor</span></div>
          <button type="button" className={`po-kpi ${focusFilter === "waiting" ? "on" : ""}`} aria-pressed={focusFilter === "waiting"} onClick={() => toggleFocus("waiting")}>
            <span className="l">Talep sahibinde</span><span className="v">{kpi.waiting}</span><span className="s">bilgi bekleniyor · <b>{kpi.waitingLate}</b> tanesi 7 günü geçti</span>
          </button>
          <div className="po-kpi ok"><span className="l">Son 30 gün tamamlanan</span><span className="v">{kpi.done.n}</span><span className="s">{kpi.done.n > 0 ? <>ortalama <b>{kpi.done.avg} günde</b> çözüldü</> : "henüz yok"}</span></div>
        </section>
      ) : (
        <section className="po-kpis">
          <div className="po-kpi navy"><span className="l">Açık taleplerim</span><span className="v">{kpi.open}</span><span className="s"><b>{kpi.review}</b> inceleniyor · <b>{kpi.planned}</b> planlı/geliştiriliyor</span></div>
          <button type="button" className={`po-kpi ${kpi.waiting > 0 ? "err" : ""} ${focusFilter === "waiting" ? "on" : ""}`} aria-pressed={focusFilter === "waiting"} onClick={() => toggleFocus("waiting")}>
            <span className="l">Sizden yanıt bekleyen</span><span className="v">{kpi.waiting}</span><span className="s">{kpi.waiting > 0 ? "bilgi istendi · göstermek için tıklayın" : "bekleyen soru yok"}</span>
          </button>
          <div className="po-kpi ok"><span className="l">Son 30 gün tamamlanan</span><span className="v">{kpi.done.n}</span><span className="s">{kpi.done.n > 0 ? <>ortalama <b>{kpi.done.avg} günde</b> çözüldü</> : "henüz yok"}</span></div>
          <div className="po-kpi"><span className="l">Toplam talebiniz</span><span className="v">{kpi.total}</span><span className="s">{kpi.closed} kapalı</span></div>
        </section>
      ))}

      {attention.length > 0 && (
        <div className="po-section">
          <h2>{handler ? "Önce bunlar" : "Sizden yanıt bekleyen talepler"}</h2>
          <div className="po-upcoming">
            {attention.map(({ r, f }) => (
              <button type="button" key={r.devRequestId} className={`po-up ${f[0].state}`} onClick={() => openDrawer(r.devRequestId)}>
                <span className="po-mark" style={{ ["--po-hue" as any]: DR_TYPE[r.type].hue }} aria-hidden="true">{DR_TYPE[r.type].glyph}</span>
                <div><b>{r.title}</b><small>{f[0].text} · {r.number} · {r.requesterName}</small></div>
                <span className="amt">{handler ? (r.priority ? DR_PRIORITY_LABEL[r.priority] : "—") : DR_STATUS_LABEL[r.status]}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <section className={`po-card ${busy ? "dim" : ""}`}>
        <div className="po-toolbar">
          <div className="po-search">{I.search}<input placeholder="Talep no, başlık, açıklama, ekran veya kişi ara" aria-label="Talep ara" value={q} onChange={(e) => setQ(e.target.value)} />{q && <button type="button" className="po-ib" aria-label="Temizle" onClick={() => setQ("")}>{I.close}</button>}</div>
          <div className="po-chipgroup" role="group" aria-label="Durum">
            {CHIPS.map(([k, l]) => <button type="button" key={k} className={chip === k ? "on" : ""} aria-pressed={chip === k} onClick={() => setChip(k)}>{l}<i>{counts[k]}</i></button>)}
          </div>
          {seeAll && (
            <div className="po-chipgroup" role="group" aria-label="Kapsam">
              {SCOPES.map(([k, l]) => (
                <button type="button" key={k} className={scope === k ? "on" : ""} aria-pressed={scope === k} onClick={() => setScope(k)}>{l}</button>
              ))}
            </div>
          )}
          <select className="po-select" aria-label="Tür" style={{ width: "auto" }} value={typeFilter} onChange={(e) => setTypeFilter(Number(e.target.value) as T | 0)}>
            <option value={0}>Tüm türler</option>
            {DR_TYPES.map((t) => <option key={t} value={t}>{DR_TYPE[t].label}</option>)}
          </select>
          {handler && (blockingCount > 0 || blockingOnly) && <button type="button" className={`po-chip danger ${blockingOnly ? "on" : ""}`} aria-pressed={blockingOnly} onClick={toggleBlocking}>{I.warn} İşi durduran <i>{blockingCount}</i></button>}
          {filterOn && <button type="button" className="po-clear" onClick={resetFilters}>{I.filterOff} Temizle</button>}
        </div>
        <div className="po-card-head">
          <span><b>{rows.length}</b> talep</span>
          <span className="po-hint">{rows.length > 0 && (handler ? <><b>{head.urgent}</b> acil · <b>{head.high}</b> yüksek öncelikli</> : <><b>{head.open}</b> açık</>)}</span>
        </div>
        {rows.length === 0 ? (
          !loaded ? (
            <div className="po-blank"><span className="po-blank-icon">{I.warn}</span><b>Talepler yüklenemedi</b>Bağlantıyı kontrol edip yeniden deneyin.
              <button type="button" className="po-btn" onClick={refresh} disabled={busy}>{I.refresh} Yeniden dene</button></div>
          ) : items.length === 0 ? (
            canCreate && !handler ? (
              <div className="po-blank"><span className="po-blank-icon">{I.inbox}</span><b>Henüz talebiniz yok</b>Bir hata gördüğünüzde ya da bir iyileştirme istediğinizde buradan bildirin. Ekran görüntüsünü Ctrl+V ile yapıştırabilirsiniz.
                <button type="button" className="po-btn primary" onClick={newRequest}>{I.plus} İlk talebi oluştur</button></div>
            ) : (
              <div className="po-blank"><span className="po-blank-icon">{I.inbox}</span><b>Henüz talep yok</b>
                {handler ? "Kullanıcılar talep açtığında burada sıralanır; yeni talepler size bildirilir." : "Kullanıcılar talep açtığında burada sıralanır."}</div>
            )
          ) : (
            <div className="po-blank"><span className="po-blank-icon">{I.inbox}</span><b>Süzgeçle eşleşen talep yok</b>Süzgeci değiştirin.
              <button type="button" className="po-btn" onClick={resetFilters}>Süzgeci sıfırla</button></div>
          )
        ) : (
          <div className="po-table-wrap">
            <table className="po-table">
              <thead><tr><th>Talep</th><th className="po-hide-sm">Talep eden</th>{handler && <th className="po-hide-md">Öncelik</th>}<th className="po-hide-lg">Son hareket</th><th>Durum</th><th className="act" /></tr></thead>
              <tbody>
                {rows.map((r) => {
                  const id = r.devRequestId;
                  const open = drIsOpen(r.status);
                  const late = lateIds.has(id);
                  const t = DR_TYPE[r.type];
                  const lastBy = drLastBy(r);
                  const waitDays = r.status === S.New || r.status === S.InReview || r.status === S.NeedsInfo ? daysSince(r.statusChangedAt) : null;
                  return (
                    <tr key={id} className={[drawerId === id ? "on" : "", late ? "late" : "", open ? "" : "off"].join(" ")} onClick={() => openDrawer(id)}>
                      <td><div className="po-row">
                        <span className={`po-mark ${open ? "" : "off"}`} style={{ ["--po-hue" as any]: t.hue }} aria-hidden="true">{t.glyph}</span>
                        <div>
                          <div className="po-code">{r.number}{open && r.impact === Im.Blocking && <span className="po-badge err" style={{ marginLeft: "0.4rem" }}>İşi durduruyor</span>}</div>
                          <div className="po-name">{r.title}</div>
                          <div className="po-sub">{t.label} · {r.screenName ?? "Genel"}</div>
                          <div className="po-sub po-only-sm">{DR_STATUS_LABEL[r.status]} · {r.requesterName}</div>
                        </div>
                      </div></td>
                      <td className="po-hide-sm"><div className="po-name">{r.requesterName}</div><div className="po-sub">{drFmtDate(r.createdAt)}</div></td>
                      {handler && <td className="po-hide-md">{r.priority ? <span className={`po-badge ${DR_PRIORITY_BADGE[r.priority]}`}>{DR_PRIORITY_LABEL[r.priority]}</span> : "—"}</td>}
                      <td className="po-hide-lg"><span title={drFmtDateTime(r.lastActivityAt)}>{relTime(r.lastActivityAt)}</span>{lastBy && <div className="po-sub">{lastBy}</div>}</td>
                      <td>
                        <span className={`po-st ${DR_STATUS_CLASS[r.status]}`}>{DR_STATUS_LABEL[r.status]}</span>
                        {waitDays != null && waitDays > 0 && <div className={`po-sub ${late ? "po-due late" : ""}`}>{waitDays} gündür</div>}
                      </td>
                      <td className="act"><div className="po-rowacts">
                        {r.commentCount > 0 && <span className="dr-count" title={`${r.commentCount} mesaj`}>{I.msg}{r.commentCount}</span>}
                        {r.attachmentCount > 0 && <span className="dr-count" title={`${r.attachmentCount} dosya`}>{I.clip}{r.attachmentCount}</span>}
                        <button type="button" className="po-ib" title="Talep kartı" aria-label={`${r.number} talep kartı`} onClick={(e) => { e.stopPropagation(); openDrawer(id); }}>{I.eye}</button>
                      </div></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Katmanlar */}
      {drawerId && (
        <DevRequestDrawer id={drawerId} preview={items.find((r) => r.devRequestId === drawerId)} refreshKey={refreshKey}
          escDisabled={confirm.isOpen || modal.open || !!action} drafts={drafts.current}
          onClose={closeDrawer} onChanged={onDrawerChanged} onEdit={editRequest} onAction={startAction} onDelete={deleteRequest} />
      )}
      {modal.open && <DevRequestModal request={modal.request} onClose={() => setModal({ open: false, request: null })} onSaved={onModalSaved} />}
      {action && <DevRequestActionModal request={action.request} to={action.to} onClose={() => setAction(null)} onDone={onActionDone} />}
      <AlertModal isOpen={alert.isOpen} message={alert.message} type={alert.type} onClose={() => setAlert((a) => ({ ...a, isOpen: false }))} />
      <ConfirmModal isOpen={confirm.isOpen} title={confirm.title} message={confirm.message} type={confirm.type} confirmText={confirm.confirmText} cancelText="Vazgeç" onConfirm={confirm.onConfirm} onCancel={closeConfirm} />
    </div>
  );
};

export default DevRequests;
